import React, { useState, useCallback } from "react";
import axios from "axios";
import confirmAction from "../../utils/confirm";

const csrfToken = () => document.querySelector("[name=csrf-token]")?.content;

const axiosConfig = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken() },
});

const axiosMultipart = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken(), "Content-Type": "multipart/form-data" },
});

const AchievementEditor = ({ achievement, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, locale }) => {
  const [label, setLabel] = useState(achievement.label);
  const [count, setCount] = useState(achievement.count);
  const [saving, setSaving] = useState(false);
  const [icon, setIcon] = useState(achievement.icon);

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append("label", label);
    formData.append("count", count);
    try {
      await axios.patch(`/${locale}/admin/about_us/achievements/${achievement.id}`, formData, axiosMultipart());
      onUpdate({ ...achievement, label, count });
    } catch (e) {
      console.error("Failed to save achievement:", e);
    }
    setSaving(false);
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    const formData = new FormData();
    formData.append("icon", file);
    formData.append("label", label);
    formData.append("count", count);
    try {
      await axios.patch(`/${locale}/admin/about_us/achievements/${achievement.id}`, formData, axiosMultipart());
      const dataRes = await axios.get(`/${locale}/admin/about_us.json`, axiosConfig());
      const updated = dataRes.data.achievements.find((a) => a.id === achievement.id);
      if (updated) setIcon(updated.icon);
    } catch (e) {
      console.error("Failed to upload icon:", e);
    }
    setSaving(false);
    e.target.value = "";
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Achievement: {achievement.label}</h5>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isFirst} onClick={onMoveUp}>&#9650;</button>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isLast} onClick={onMoveDown}>&#9660;</button>
            <button className="btn btn-sm btn-danger" onClick={() => confirmAction(() => onDelete(achievement.id))}>Delete</button>
          </div>
        </div>

        <div className="row">
          <div className="col-md-2 text-center">
            {icon && icon.url ? (
              <img src={icon.url} alt={icon.filename} style={{ maxHeight: 80 }} className="mb-2" />
            ) : (
              <div className="text-muted mb-2">No icon</div>
            )}
            <input type="file" className="form-control-file" accept="image/*" onChange={handleIconUpload} />
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Label</label>
              <input type="text" className="form-control" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Count</label>
              <input type="number" className="form-control" value={count} min="0" onChange={(e) => setCount(parseInt(e.target.value, 10) || 0)} />
            </div>
          </div>
        </div>

        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Achievement"}
        </button>
      </div>
    </div>
  );
};

const AboutUsAchievementsManager = ({ initialAchievements, locale }) => {
  const [achievements, setAchievements] = useState(initialAchievements || []);
  const [addingAchievement, setAddingAchievement] = useState(false);
  const [newAchLabel, setNewAchLabel] = useState("");

  const reorderAchievements = useCallback(async (newAchs) => {
    setAchievements(newAchs);
    const ids = newAchs.map((a) => a.id);
    try {
      await axios.patch(`/${locale}/admin/about_us/achievements/reorder`, { ids }, axiosConfig());
    } catch (e) {
      console.error("Failed to reorder achievements:", e);
    }
  }, [locale]);

  const moveItem = (list, index, direction) => {
    const newList = [...list];
    const swapIndex = index + direction;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    reorderAchievements(newList);
  };

  const handleAddAchievement = async () => {
    if (!newAchLabel.trim()) return;
    setAddingAchievement(true);
    try {
      const res = await axios.post(`/${locale}/admin/about_us/achievements`, { label: newAchLabel }, axiosConfig());
      setAchievements([...achievements, { ...res.data, icon: null }]);
      setNewAchLabel("");
    } catch (e) {
      console.error("Failed to create achievement:", e);
    }
    setAddingAchievement(false);
  };

  const handleDeleteAchievement = async (id) => {
    try {
      await axios.delete(`/${locale}/admin/about_us/achievements/${id}`, axiosConfig());
      setAchievements(achievements.filter((a) => a.id !== id));
    } catch (e) {
      console.error("Failed to delete achievement:", e);
    }
  };

  const handleUpdateAchievement = (updatedAch) => {
    setAchievements(achievements.map((a) => (a.id === updatedAch.id ? { ...a, ...updatedAch } : a)));
  };

  return (
    <div className="mt-3">
      {achievements.map((ach, index) => (
        <AchievementEditor
          key={ach.id}
          achievement={ach}
          locale={locale}
          onUpdate={handleUpdateAchievement}
          onDelete={handleDeleteAchievement}
          onMoveUp={() => moveItem(achievements, index, -1)}
          onMoveDown={() => moveItem(achievements, index, 1)}
          isFirst={index === 0}
          isLast={index === achievements.length - 1}
        />
      ))}

      <div className="card mb-4">
        <div className="card-body">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="New achievement label (e.g. Championship Wins)"
              value={newAchLabel}
              onChange={(e) => setNewAchLabel(e.target.value)}
            />
            <div className="input-group-append">
              <button className="btn btn-success" onClick={handleAddAchievement} disabled={addingAchievement || !newAchLabel.trim()}>
                {addingAchievement ? "Adding..." : "Add Achievement"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUsAchievementsManager;
